import { useState, useMemo } from "react";
import useUrlState from "@/hooks/useUrlState";
import { CarrierTestRecord, CARRIER_STYLES } from "../types";

const ITEMS_PER_PAGE = 10;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ampm = d.getHours() >= 12 ? "오후" : "오전";
  const h12 = d.getHours() % 12 || 12;
  return `${y}. ${m}. ${day}. ${ampm} ${h12}:${mm}:${ss}`;
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}.${day} ${hh}:${mm}`;
}

// Helper: get final delivery status considering both review_results and results
function getCarrierStatus(record: CarrierTestRecord, carrier: string): "pending" | "success" | "spam" | "delivered" {
  const review = record.review_results?.find((r) => r.carrier === carrier);
  if (review) return review.status as "success" | "spam";
  const result = record.results?.find((r) => r.carrier === carrier);
  const ds = result?.delivery_status;
  if (ds === "delivered" || ds === "confirmed") return "delivered";
  return "pending";
}

function Pagination({
  current,
  total,
  onChange,
}: {
  current: number;
  total: number;
  onChange: (p: number) => void;
}) {
  if (total <= 1) return null;

  const pages: (number | "...")[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push("...");
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push("...");
    pages.push(total);
  }

  return (
    <div className="flex items-center justify-center gap-1 pt-3">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current <= 1}
        className="w-7 h-7 flex items-center justify-center rounded-md border border-white/[0.08] text-gray-400 text-xs hover:bg-white/[0.05] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
      >
        <i className="ri-arrow-left-s-line text-sm"></i>
      </button>
      {pages.map((p, idx) =>
        p === "..." ? (
          <span key={`dots-${idx}`} className="w-7 h-7 flex items-center justify-center text-gray-600 text-xs">
            ...
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium cursor-pointer transition-colors ${
              p === current
                ? "bg-violet-600 text-white"
                : "text-gray-400 hover:bg-white/[0.05] border border-white/[0.06]"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current >= total}
        className="w-7 h-7 flex items-center justify-center rounded-md border border-white/[0.08] text-gray-400 text-xs hover:bg-white/[0.05] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
      >
        <i className="ri-arrow-right-s-line text-sm"></i>
      </button>
    </div>
  );
}

export default function TestHistory({
  records,
  activeTab,
  onTabChange,
}: {
  records: CarrierTestRecord[];
  activeTab?: "grouped" | "list";
  onTabChange?: (tab: "grouped" | "list") => void;
}) {
  const [internalTab, setInternalTab] = useState<"grouped" | "list">("grouped");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // 페이지 번호도 주소(URL)에 남겨 새로고침·뒤로 가기·탭 복귀에도 보던 페이지 그대로 (탭별로 분리)
  const [groupedPageRaw, setGroupedPageRaw] = useUrlState("ct_gpage", "1");
  const [listPageRaw, setListPageRaw] = useUrlState("ct_lpage", "1");

  const tab = activeTab ?? internalTab;

  const parsePage = (v: string) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };
  const page = tab === "grouped" ? parsePage(groupedPageRaw) : parsePage(listPageRaw);
  const setPage = (p: number) => {
    if (tab === "grouped") setGroupedPageRaw(String(p));
    else setListPageRaw(String(p));
  };

  const toggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Aggregate stats — based on review_results first, then results.delivery_status
  const stats = records.reduce(
    (acc, r) => {
      const carriers = [...new Set((r.recipients || []).map((x: any) => x.carrier).filter(Boolean))];
      carriers.forEach((c) => {
        const ds = getCarrierStatus(r, c);
        if (!acc[c]) acc[c] = { total: 0, success: 0, spam: 0, pending: 0, delivered: 0 };
        acc[c].total++;
        if (ds === "success") acc[c].success++;
        else if (ds === "spam") acc[c].spam++;
        else if (ds === "delivered") acc[c].delivered++;
        else acc[c].pending++;
      });
      return acc;
    },
    {} as Record<string, { total: number; success: number; spam: number; pending: number; delivered: number }>
  );

  // Flatten all results for list view — using review_results as primary source
  const flatResults = useMemo(
    () =>
      records.flatMap((r) =>
        (r.results || []).map((res) => ({
          ...res,
          delivery_status: getCarrierStatus(r, res.carrier),
          sendId: r.id,
          sentAt: r.created_at,
          message: r.message,
        }))
      ),
    [records]
  );

  // Pagination for grouped tab
  const totalGroupedPages = Math.ceil(records.length / ITEMS_PER_PAGE);
  const groupedPage = Math.min(page, Math.max(1, totalGroupedPages));
  const paginatedRecords = records.slice(
    (groupedPage - 1) * ITEMS_PER_PAGE,
    groupedPage * ITEMS_PER_PAGE
  );

  // Pagination for list tab
  const totalListPages = Math.ceil(flatResults.length / ITEMS_PER_PAGE);
  const listPage = Math.min(page, Math.max(1, totalListPages));
  const paginatedFlatResults = flatResults.slice(
    (listPage - 1) * ITEMS_PER_PAGE,
    listPage * ITEMS_PER_PAGE
  );

  const handleTabChange = (newTab: "grouped" | "list") => {
    if (onTabChange) {
      onTabChange(newTab);
    } else {
      setInternalTab(newTab);
    }
    setExpandedId(null);
  };

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-3">
        {(["SKT", "KT", "LGU+"] as const).map((carrier) => {
          const s = stats[carrier] || { total: 0, success: 0, spam: 0, pending: 0, delivered: 0 };
          const style = CARRIER_STYLES[carrier];
          return (
            <div
              key={carrier}
              className={`rounded-xl border ${style.border} ${style.bg} px-3 py-3`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`text-xs font-bold ${style.text}`}>{carrier}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-[10px]">
                  <span className="text-gray-500">발송</span>
                  <span className="text-gray-300">{s.total}</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-emerald-400">성공</span>
                  <span className="text-emerald-400">{s.success}</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-sky-400">배달 확인</span>
                  <span className="text-sky-400">{s.delivered}</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-rose-400">스팸</span>
                  <span className="text-rose-400">{s.spam}</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-amber-400">진행중</span>
                  <span className="text-amber-400">{s.pending}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-white/[0.06]">
        <button
          onClick={() => handleTabChange("grouped")}
          className={`px-1 py-2 text-xs font-medium border-b-2 cursor-pointer whitespace-nowrap transition-colors ${
            tab === "grouped"
              ? "text-white border-violet-500"
              : "text-gray-500 border-transparent hover:text-gray-300"
          }`}
        >
          발송별 보기
        </button>
        <button
          onClick={() => handleTabChange("list")}
          className={`px-1 py-2 text-xs font-medium border-b-2 cursor-pointer whitespace-nowrap transition-colors ${
            tab === "list"
              ? "text-white border-violet-500"
              : "text-gray-500 border-transparent hover:text-gray-300"
          }`}
        >
          전체 목록
        </button>
      </div>

      {/* --- Tab: Grouped by Send --- */}
      {tab === "grouped" && (
        <div className="space-y-2">
          {records.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/[0.04] mb-3">
                <i className="ri-smartphone-line text-xl text-gray-500"></i>
              </div>
              <p className="text-gray-400 text-sm font-medium">테스트 발송 내역이 없습니다</p>
              <p className="text-gray-600 text-xs mt-1">왼쪽에서 테스트 문자를 발송하면 결과가 여기에 표시됩니다</p>
            </div>
          ) : (
            <>
              {paginatedRecords.map((r) => {
                const isExpanded = expandedId === r.id;
                const resultByCarrier: Record<string, CarrierTestRecord["results"]> = {};
                (r.results || []).forEach((res) => {
                  if (!resultByCarrier[res.carrier]) resultByCarrier[res.carrier] = [];
                  resultByCarrier[res.carrier].push(res);
                });
                const carrierCount = Object.keys(resultByCarrier).length;

                return (
                  <div
                    key={r.id}
                    className="rounded-xl border border-white/[0.06] bg-[#13131f] overflow-hidden"
                  >
                    <button
                      onClick={() => toggle(r.id)}
                      className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-gray-300 text-sm font-medium">
                          {fmtDate(r.created_at)}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-violet-500/15 text-violet-400 text-[10px] font-medium border border-violet-500/20">
                          {carrierCount}개 통신사
                        </span>
                      </div>
                      <i
                        className={`ri-arrow-down-s-line text-gray-500 text-lg transition-transform ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      ></i>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-white/[0.06] pt-3">
                        <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] px-3 py-3">
                          <p className="text-gray-400 text-[10px] mb-1">메시지</p>
                          <p className="text-gray-300 text-xs whitespace-pre-wrap leading-relaxed">{r.message}</p>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(resultByCarrier).map(([carrier, results]) => {
                            const style = CARRIER_STYLES[carrier as keyof typeof CARRIER_STYLES] || CARRIER_STYLES.SKT;
                            const statuses = results.map((x) => getCarrierStatus(r, x.carrier));
                            const allSuccess = statuses.every((s) => s === "success");
                            const allSpam = statuses.every((s) => s === "spam");
                            const allPending = statuses.every((s) => s === "pending");
                            const allDelivered = statuses.every((s) => s === "delivered");
                            const hasMixed = !allSuccess && !allSpam && !allPending && !allDelivered;
                            const mixedText = hasMixed
                              ? statuses
                                  .filter((s, i, a) => a.indexOf(s) === i)
                                  .map((s) => (s === "success" ? "성공" : s === "spam" ? "스팸" : s === "delivered" ? "배달 확인" : "진행중"))
                                  .join(" / ")
                              : "";

                            const statusColor = allSuccess
                              ? "text-emerald-400"
                              : allSpam
                              ? "text-rose-400"
                              : allDelivered
                              ? "text-sky-400"
                              : allPending
                              ? "text-amber-400"
                              : "text-gray-400";
                            const statusIcon = allSuccess
                              ? "ri-checkbox-circle-fill"
                              : allSpam
                              ? "ri-close-circle-fill"
                              : allDelivered
                              ? "ri-truck-fill"
                              : allPending
                              ? "ri-time-fill"
                              : "ri-question-mark";
                            const statusLabel = allSuccess
                              ? "성공"
                              : allSpam
                              ? "스팸"
                              : allDelivered
                              ? "배달 확인됨"
                              : allPending
                              ? "진행중"
                              : mixedText;

                            return (
                              <div
                                key={carrier}
                                className={`rounded-lg border ${style.border} ${style.bg} px-3 py-2.5`}
                              >
                                <div className="flex items-center gap-1.5 mb-2">
                                  <span className={`text-xs font-bold ${style.text}`}>{carrier}</span>
                                  <span className="text-[10px] text-gray-500">▶ 발송완료</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <i className={`${statusIcon} ${statusColor} text-xs`}></i>
                                  <span className={`text-[10px] font-medium ${statusColor}`}>
                                    {statusLabel}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <Pagination current={groupedPage} total={totalGroupedPages} onChange={setPage} />
            </>
          )}
        </div>
      )}

      {/* --- Tab: Full List --- */}
      {tab === "list" && (
        <div className="space-y-1">
          {flatResults.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/[0.04] mb-3">
                <i className="ri-smartphone-line text-xl text-gray-500"></i>
              </div>
              <p className="text-gray-400 text-sm font-medium">테스트 발송 내역이 없습니다</p>
              <p className="text-gray-600 text-xs mt-1">왼쪽에서 테스트 문자를 발송하면 결과가 여기에 표시됩니다</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-[10px] text-gray-500">
                  총 {flatResults.length}건 중 {(listPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(listPage * ITEMS_PER_PAGE, flatResults.length)}건
                </span>
              </div>

              {/* Table header */}
              <div className="hidden md:grid grid-cols-[1fr_70px_100px_1fr_70px] gap-2 px-3 py-2 text-[10px] text-gray-500 font-medium border-b border-white/[0.04]">
                <span>통신사</span>
                <span>번호</span>
                <span>시간</span>
                <span>메시지</span>
                <span className="text-right">결과</span>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {paginatedFlatResults.map((item, idx) => {
                  const style = CARRIER_STYLES[item.carrier as keyof typeof CARRIER_STYLES] || CARRIER_STYLES.SKT;
                  const ds = item.delivery_status || "pending";
                  const isSuccess = ds === "success";
                  const isSpam = ds === "spam";
                  const isDelivered = ds === "delivered";
                  const isPend = !isSuccess && !isSpam && !isDelivered;
                  return (
                    <div
                      key={`m-${item.sendId}-${item.carrier}-${idx}`}
                      className="rounded-xl border border-white/[0.06] bg-[#13131f] px-4 py-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${style.dot} shrink-0`}></span>
                          <span className={`text-xs font-bold ${style.text}`}>{item.carrier}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <i
                            className={`${
                              isSuccess
                                ? "ri-checkbox-circle-fill text-emerald-400"
                                : isSpam
                                ? "ri-close-circle-fill text-rose-400"
                                : isDelivered
                                ? "ri-truck-fill text-sky-400"
                                : "ri-time-fill text-amber-400"
                            } text-xs`}
                          ></i>
                          <span
                            className={`text-[10px] font-medium ${
                              isSuccess ? "text-emerald-400" : isSpam ? "text-rose-400" : isDelivered ? "text-sky-400" : "text-amber-400"
                            }`}
                          >
                            {isSuccess ? "성공" : isSpam ? "스팸" : isDelivered ? "배달 확인" : "진행중"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="text-gray-400">{item.phone.replace(/(\d{3})[-]?(\d{4})[-]?(\d{4})/, '$1-xxxx-$3')}</span>
                        <span className="text-gray-600">·</span>
                        <span className="text-gray-500">{fmtShortDate(item.sentAt)}</span>
                      </div>
                      <p className="text-[11px] text-gray-300 leading-relaxed line-clamp-2">{item.message}</p>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table rows */}
              {paginatedFlatResults.map((item, idx) => {
                const style = CARRIER_STYLES[item.carrier as keyof typeof CARRIER_STYLES] || CARRIER_STYLES.SKT;
                const ds = item.delivery_status || "pending";
                const isSuccess = ds === "success";
                const isSpam = ds === "spam";
                const isDelivered = ds === "delivered";
                const isPend = !isSuccess && !isSpam && !isDelivered;
                return (
                  <div
                    key={`${item.sendId}-${item.carrier}-${idx}`}
                    className="hidden md:grid grid-cols-[1fr_70px_100px_1fr_70px] gap-2 px-3 py-2.5 items-center border-b border-white/[0.02] hover:bg-white/[0.02] transition-colors rounded-lg"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${style.dot} shrink-0`}></span>
                      <span className={`text-xs font-bold ${style.text}`}>{item.carrier}</span>
                    </div>
                    <span className="text-[10px] text-gray-400 truncate">{item.phone.replace(/(\d{3})[-]?(\d{4})[-]?(\d{4})/, '$1-xxxx-$3')}</span>
                    <span className="text-[10px] text-gray-500">{fmtShortDate(item.sentAt)}</span>
                    <span className="text-[10px] text-gray-300 truncate">{item.message}</span>
                    <div className="flex items-center justify-end gap-1">
                      <i
                        className={`${
                          isSuccess
                            ? "ri-checkbox-circle-fill text-emerald-400"
                            : isSpam
                            ? "ri-close-circle-fill text-rose-400"
                            : isDelivered
                            ? "ri-truck-fill text-sky-400"
                            : "ri-time-fill text-amber-400"
                        } text-xs`}
                      ></i>
                      <span
                        className={`text-[10px] font-medium ${
                          isSuccess ? "text-emerald-400" : isSpam ? "text-rose-400" : isDelivered ? "text-sky-400" : "text-amber-400"
                        }`}
                      >
                        {isSuccess ? "성공" : isSpam ? "스팸" : isDelivered ? "배달 확인" : "진행중"}
                      </span>
                    </div>
                  </div>
                );
              })}

              <Pagination current={listPage} total={totalListPages} onChange={setPage} />
            </>
          )}
        </div>
      )}
    </div>
  );
}